<template>
  <debug-code v-model:dynamic-title="field.dynamicTitle">
    <!-- 设置:key="tableColumns.length"是为了在列个数发生变化时，重新实例化table，从而重新计算列宽 -->
    <s-table
      :dataSource="tableDataSource"
      :columns="tableColumns"
      :autoRowHeight="true"
      :bordered="true"
      stripe
      summary-fixed
      :scroll="{ x: totalWidth }"
      size="small"
      row-key="_key"
      :key="tableColumns.length"
      v-model:pagination="pagination"
      :rangeSelection="false"
    >
      <template #headerCell="{ column }">
        <span>{{ column.title }}</span>
        <span
          v-if="column.has_reqrired"
          class="required-star"
        >
          *
        </span>
      </template>
      <template #bodyCell="{ column, record }">
        <template v-if="column.key === 'action'">
          <div
            class="cell-item action-cell"
            v-if="!lockStructure"
          >
            <a-button
              @click="onCopyRow(record._key)"
              size="small"
              type="link"
              :disabled="field.max_count && value.length >= field.max_count"
            >
              <template #icon>
                <CopyOutlined />
              </template>
            </a-button>
            <a-button
              @click="onDeleteRow(record._key)"
              size="small"
              type="link"
              danger
              :disabled="value.length <= min_count"
            >
              <template #icon>
                <DeleteOutlined />
              </template>
            </a-button>
          </div>
        </template>
        <template v-else-if="column.key === 'index'">
          <div class="cell-item">
            {{ record._key + 1 }}
          </div>
        </template>
        <table-cell
          v-else-if="column.field"
          :key="`${record._key}_${column.dataIndex}`"
          :field="column.field"
          :model="value[record._key]"
          :validateInfos="validateInfosList[record._key] || {}"
          :options="field.form?.options || {}"
          :context="context"
          :innerModel="_innerModel[record._key]"
          :globalInnerModel="globalInnerModel"
          :nestValidate="nestValidate"
          :baseKeyPath="[...baseKeyPath, field.name, record._key]"
          :workerHostFunctions="workerHostFunctions"
          :is_form_design="is_form_design"
          :has_reqrired="column.has_reqrired"
          @update_model="params => onUpdateModel(record._key, params)"
        />
      </template>
      <template
        #summary
        v-if="summaryColumns.length > 0"
      >
        <s-table-summary-row>
          <s-table-summary-cell :index="0">合计</s-table-summary-cell>
          <s-table-summary-cell
            v-for="item in summaryColumns"
            :key="item.index"
            :index="item.index"
          >
            <template #default="vars">
              <TableSummary
                :summary-params="vars"
                :tableDataSource="tableDataSource"
              ></TableSummary>
            </template>
          </s-table-summary-cell>
        </s-table-summary-row>
      </template>
    </s-table>
  </debug-code>
</template>
<script setup lang="ts">
import TableCell from './TableCell.vue';
import TableSummary from './TableSummary.vue';
import { computed, ref, reactive, watch, onUnmounted, onMounted } from 'vue';
import { useForm } from 'ant-design-vue/es/form';
import debugCode from '../debugCode.vue';
import { executeScriptInWorker } from 'liyu-pc-base/utils/function';
import { debounce, cloneDeep } from 'lodash-es';
import { resolveRemoteSelectProLabels } from '../RemoteSelectPro/labelResolver';

const pagination = ref<any>({
  showQuickJumper: true,
  showSizeChanger: false,
  showTotal: total => `共 ${total} 条记录`,
  pageSize: 5,
  current: 1,
});
interface Props {
  value: any[];
  field: Record<string, any>;
  context?: Record<string, any>;
  innerModel?: Record<string, any>;
  globalInnerModel?: Record<string, any>;
  nestValidate?: Record<string, any> | null;
  baseKeyPath?: any[];
  workerHostFunctions?: Record<string, any>;
  is_form_design?: boolean;
  min_count?: number;
  formModel: Record<string, any>;
  // 修改模式传入 true：隐藏行删除按钮，禁止增删行
  lockStructure?: boolean;
}

const props = defineProps<Props>();
const emit = defineEmits<{
  (e: 'remove', index: number): void;
  /** 复制行：把 index 行的数据插入到 index + 1 行 */
  (e: 'copy', index: number): void;
  /** 所有行 remoteselectpro 的 label / innerModel 回填完成，可安全计算只读动态值 */
  (e: 'labels_resolved'): void;
}>();

const rulesRef = reactive({ ...(props.field.form?.rules || {}) });
const validateInfosList = ref<any[]>([]);
const validateFunctions = ref<any[]>([]);
const removeRulKeysList = ref<any[]>([]);

const genID = (): string => {
  const array = new Uint8Array(16); // 16 字节 = 128 位
  window.crypto.getRandomValues(array);
  return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
};
const valite_id = genID();
const tableColumns = ref([]);

const syncValidate = () => {
  const newValidateInfosList: any[] = [];
  const newValidateFunctions: any[] = [];

  (props.value || []).forEach((item: any, index: number) => {
    if (!removeRulKeysList.value[index]) {
      removeRulKeysList.value[index] = {};
    }
    const rowRemoveRulKeys = removeRulKeysList.value[index];

    const new_rules_obj = {};
    Object.keys(rulesRef).forEach(key => {
      if (key in item && !rowRemoveRulKeys[key]) {
        new_rules_obj[key] = rulesRef[key];
      }
    });

    const { validate, validateInfos } = useForm(item, new_rules_obj);
    newValidateInfosList[index] = validateInfos;
    newValidateFunctions[index] = validate;
  });

  validateInfosList.value = newValidateInfosList;
  validateFunctions.value = newValidateFunctions;

  if (props.nestValidate) {
    props.nestValidate[valite_id] = async () => {
      const promises = newValidateFunctions.map(v => v());
      return Promise.all(promises);
    };
  }
};

const onUpdateModel = (index: number, params: any) => {
  if (!removeRulKeysList.value[index]) {
    removeRulKeysList.value[index] = {};
  }
  if (params.add_key) {
    // 添加字段时，不忽略校验，所以需要从removeRulKeys中删除
    delete removeRulKeysList.value[index][params.add_key];
    syncValidate();
  } else if (params.remove_key) {
    // 删除字段时，忽略校验，所以需要添加到removeRulKeys中
    removeRulKeysList.value[index][params.remove_key] = true;
    syncValidate();
  }
};

watch(
  () => props.value,
  () => {
    syncValidate();
  },
  { deep: true, immediate: true },
);
watch(
  () => props.field,
  () => {
    genTableColumns();
  },
  { deep: true },
);
watch(
  () => props.formModel,
  () => {
    genTableColumns();
  },
  { deep: true },
);
watch(
  () => props.context,
  () => {
    genTableColumns();
  },
  { deep: true },
);
onMounted(() => {
  genTableColumns();
  // 初始数据（如回显已有大量行）时，非当前页行未挂载组件，同样需要 label 兜底解析
  resolveLabels();
});
onUnmounted(() => {
  if (props.nestValidate) {
    delete props.nestValidate[valite_id];
  }
  resolveLabels.cancel();
  // 安全兜底：表格被卸载（如 showType 切换）时也通知父级解除等待，
  // 避免父级 pendingTableLabelBackfill 一直为 true 导致只读动态值不再计算
  emit('labels_resolved');
});
const totalWidth = computed(() => {
  let width = 0;
  tableColumns.value.forEach(col => {
    width += col._width || 150;
  });
  return width;
});
const tableDataSource = computed(() => {
  const value = props.value || [];
  return value.map((item: any, index: number) => {
    return { ...item, _key: index };
  });
});
const hasReqrired = fieldName => {
  if (props.field?.form?.rules) {
    const rule = props.field?.form?.rules[fieldName];
    if (rule) {
      for (const item of rule) {
        if (item.required) {
          return true;
        }
      }
    }
  }
  return false;
};
const genTableColumns = async () => {
  let columns = [];
  const keys = {};
  columns.push({
    title: '序号',
    dataIndex: 'index',
    key: 'index',
    maxWidth: 50,
  });
  props.field?.form?.dynamicFieldList?.forEach(group_item => {
    if (group_item.children instanceof Array) {
      group_item.children.forEach(item => {
        if (keys[item.name]) return;
        if (item.is_table_hide_column) {
          return;
        }
        keys[item.name] = true;
        let is_table_auto_width = item.is_table_auto_width;
        if (is_table_auto_width == null || is_table_auto_width === '') {
          is_table_auto_width = true;
        }
        const hasReq = hasReqrired(item.name);
        const obj: any = {
          title: item.label,
          dataIndex: item.name,
          key: item.name,
          _width: item.table_column_width || 150,
          checked: true,
          autoWidth: is_table_auto_width,
          resizable: true,
          autoHeight: true,
          field: item,
          is_summary: item.is_table_summary,
          has_reqrired: hasReq,
        };
        if (is_table_auto_width) {
          obj.minWidth = obj._width;
        } else {
          obj.width = obj._width;
        }
        columns.push(obj);
      });
    }
  });
  if (props.field.max_count !== props.min_count) {
    columns.push({
      title: '操作',
      key: 'action',
      dataIndex: 'action',
      width: 90,
      _width: 90,
      fixed: 'right',
    });
  }
  if (props.field.dynamicTitle?.enable) {
    const code = props.field.dynamicTitle.code;
    try {
      const newColumns = await executeScriptInWorker(
        code,
        {
          innerModel: props.innerModel,
          globalInnerModel: props.globalInnerModel,
          field: props.field,
          value: props.value,
          formModel: props.formModel,
          context: props.context,
          keyPath: [...props.baseKeyPath, props.field.name],
          columns: columns,
        },
        props.workerHostFunctions || {},
      );
      if (newColumns) {
        columns = newColumns;
      }
    } catch (e) {
      console.error(e);
    }
  }
  tableColumns.value = columns;
};
const onDeleteRow = (index: number) => {
  removeRulKeysList.value.splice(index, 1);
  emit('remove', index);
};
const onCopyRow = (index: number) => {
  // 校验忽略配置按行下标存放，复制时同步插入副本，保持后续行下标对齐
  removeRulKeysList.value.splice(index + 1, 0, cloneDeep(removeRulKeysList.value[index] || {}));
  emit('copy', index);
};

/**
 * 表格分页下，非当前页的行不会挂载 TableCell/RemoteSelectPro，
 * 因此这些行的 `name + '.label'`（展示字段）、name2、innerModel 永远无法解析。
 * 这里在数据变化后对所有行做一次 label 兜底解析（与 RemoteSelectPro 的查询逻辑一致），
 * 保证 Excel 导入、添加等产生的大量数据在分页场景下数据也是正常的。
 */
const collectRemoteSelectProFields = () => {
  const list: any[] = [];
  props.field?.form?.dynamicFieldList?.forEach((group_item: any) => {
    if (group_item.children instanceof Array) {
      group_item.children.forEach((item: any) => {
        if (item.type === 'remoteselectpro' && item.postUrl && !item.is_table_hide_column) {
          list.push(item);
        }
      });
    }
  });
  return list;
};

const needResolve = (row: any, field: any, rowIndex: number) => {
  if (!row) return false;
  const v = row[field.name];
  const hasValue =
    v !== undefined &&
    v !== null &&
    v !== '' &&
    !(v instanceof Array && v.length === 0);
  if (!hasValue) return false;
  // 展示 label 缺失需要回填
  const label = row[field.name + '.label'];
  const labelMissing =
    label === undefined ||
    label === null ||
    label === '' ||
    (label instanceof Array && label.length === 0);
  if (labelMissing) return true;
  // label 已存在（如 Excel 导入时直接回填了 label）但 innerModel 缺失也需回填，
  // 否则 scoped/global 只读动态值拿不到依赖的原始选项
  const rowInner = props.innerModel?.[props.field.name]?.[rowIndex];
  const innerMissing =
    !rowInner || rowInner[field.name] === undefined || rowInner[field.name] === null;
  return innerMissing;
};

const resolveLabels = debounce(async () => {
  if (props.is_form_design) return;
  try {
    const fields = collectRemoteSelectProFields();
    if (fields.length === 0 || !props.value || props.value.length === 0) return;
    const valueList = props.value;

    for (const field of fields) {
      const targets: any[] = [];
      valueList.forEach((row: any, rowIndex: number) => {
        if (needResolve(row, field, rowIndex)) {
          targets.push({ rowIndex, row });
        }
      });
      if (targets.length === 0) continue;

      const result = await resolveRemoteSelectProLabels({
        field,
        targets,
        baseKeyPath: [...(props.baseKeyPath || []), props.field.name],
        context: props.context,
      });

      Object.entries(result).forEach(([idxStr, r]) => {
        if (!r) return;
        const rowIndex = Number(idxStr);
        const row = valueList[rowIndex];
        if (!row) return;
        // 1) 展示字段 label（与 RemoteSelectPro emit('update:label') 写入一致）
        if (r.label !== undefined && r.label !== null && r.label !== '') {
          row[field.name + '.label'] = r.label;
          // 2) name2：与 FieldRender2 labelModel watch 的 update_name2 行为一致
          if (field.name2) {
            row[field.name2] = typeof r.label === 'string' ? r.label : r.label.toString();
          }
        }
        // 3) innerModel：与 RemoteSelectPro.setInnerModel 写入一致。
        //    注意：父级 FormTemplate 的 innerModel[field.name] 数组是惰性创建的
        //    （仅在 syncDynamicValues 运行时补齐），而回填发生在其之前，
        //    若这里不先补齐结构，写入会静默丢失，导致 scoped/global 动态值无数据。
        if (props.innerModel) {
          if (!props.innerModel[props.field.name]) {
            props.innerModel[props.field.name] = [];
          }
          const fieldInner = props.innerModel[props.field.name];
          if (!fieldInner[rowIndex]) {
            fieldInner[rowIndex] = {};
          }
          fieldInner[rowIndex][field.name] = r.option;
        }
      });
    }
  } finally {
    // 无论是否有数据需要回填，都通知父级：本次回填周期结束，
    // 父级 FormTemplate 此时才可安全计算依赖 innerModel 的只读动态值
    emit('labels_resolved');
  }
}, 300);

watch(
  () => [props.value, props.field, props.context],
  () => {
    resolveLabels();
  },
  { deep: true },
);
const _innerModel = computed(() => {
  if (props.innerModel) {
    const fieldInnerModel = props.innerModel[props.field.name] || [];
    return (props.value || []).map((_, i) => fieldInnerModel[i] || {});
  }
  return [];
});
const summaryColumns = computed(() => {
  const list = [];
  let index = 0;
  for (const item of tableColumns.value) {
    if (item.is_summary) {
      list.push({
        index: index,
      });
    }
    index++;
  }
  return list;
});
</script>
<style lang="less" scoped>
:deep(.surely-table-cell) {
  .surely-table-cell-inner {
    height: 100%;
    &:has(.editable) {
      border: 1px solid transparent !important;
      background-color: rgba(248, 232, 53, 0.214);

      input {
        text-overflow: ellipsis;
        &:focus {
          text-overflow: clip;
        }
      }
    }
    &:has(.editable:hover),
    &:has(.editable:focus),
    &:has(.editable input:focus),
    &:has(.editable textarea:focus) {
      border-color: #40a9ff !important;
      box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
      background-color: rgba(252, 238, 85, 0.738);
      border-radius: 6px;
    }
    &:has(.ant-form-item-has-error) {
      border: 1px solid transparent !important;
      border-color: #ff4d4f !important;
      border-radius: 6px;
    }
    .surely-table-cell-content {
      padding: 0 !important;
    }
  }
  .ant-form-item-explain-error {
    font-size: 10px;
  }
}

.required-star {
  color: red;
  margin-left: 4px;
}

.cell-item {
  padding: 8px;
}

.action-cell {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 2px;
  padding: 8px 2px;
}
</style>
